"use client";

import React, { useRef, useEffect, useState } from "react";
import Image from "next/image";
import { useLenis } from "./providers/lenis";
import dynamic from "next/dynamic";

const PilotModal = dynamic(() => import("./pilot-modal"), {
  loading: () => null,
  ssr: false,
});

const NavBar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isFixed, setIsFixed] = useState(false);
  const [navHeight, setNavHeight] = useState(0);
  const [activeSection, setActiveSection] = useState<string>("");
  const [isPilotModalOpen, setIsPilotModalOpen] = useState(false);

  const { setNavbarHeight, scrollTo, lenis } = useLenis();
  const navRef = useRef<HTMLElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;

    // Measure immediately
    const height = el.getBoundingClientRect().height;
    setNavbarHeight(height);
    setNavHeight(height);

    // Re-measure on every resize (handles breakpoint changes)
    const resizeObserver = new ResizeObserver(([entry]) => {
      setNavbarHeight(entry.contentRect.height);
      setNavHeight(entry.contentRect.height);
    });

    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [setNavbarHeight]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
      lenis?.stop();
    } else {
      document.body.style.overflow = "";
      lenis?.start();
    }
    return () => {
      document.body.style.overflow = "";
      lenis?.start();
    };
  }, [isMobileMenuOpen, lenis]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFixed(!entry.isIntersecting && entry.boundingClientRect.top <= 0);
      },
      { rootMargin: "0px", threshold: 0 },
    );
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const sections = ["home", "about", "how-it-works", "blog", "faq"];
    const intersectingSections = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            intersectingSections.add(entry.target.id);
          } else {
            intersectingSections.delete(entry.target.id);
          }
        });

        if (intersectingSections.size > 0) {
          // Favor the highest ordered section if multiple somehow overlap our detection band
          const active = sections.find((id) => intersectingSections.has(id));
          if (active) setActiveSection(active);
        } else {
          // Clear active link entirely if user is in an un-navigable section (hero/footer/etc)
          setActiveSection("");
        }
      },
      { rootMargin: "-30% 0px -60% 0px" }, // Widened detection band to 10% height to ensure smooth handoffs
    );

    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleScrollTo = (target: string) => {
    scrollTo(target, { duration: 2 });
  };

  const navLinks = [
    { label: "Home", target: "#home", id: "home" },
    { label: "About", target: "#about", id: "about" },
    { label: "How It Works", target: "#how-it-works", id: "how-it-works" },
    { label: "Blog", target: "#blog", id: "blog" },
    { label: "FAQ", target: "#faq", id: "faq" },
  ];

  return (
    <>
      <div
        ref={sentinelRef}
        className="h-px w-full absolute top-auto"
        style={{ zIndex: -1 }}
      />

      {(isFixed || isMobileMenuOpen) && <div style={{ height: navHeight }} />}

      <nav
        ref={navRef}
        className={`left-0 z-40 right-0 transition-all duration-300 w-full ${
          isFixed || isMobileMenuOpen
            ? "fixed top-0 bg-white shadow-lg"
            : "relative bg-transparent"
        }`}
      >
        <div className="2xl:container 2xl:mx-auto px-4">
          <div className="text-surface text-xs xl:text-sm leading-normal sm:leading-none py-4 lg:py-5 xl:py-6 flex items-center justify-between font-medium">
            <div
              className="relative w-16.5 h-11 md:h-11 lg:h-11.5 xl:h-13 cursor-pointer"
              onClick={() => {
                if (isMobileMenuOpen) {
                  setIsMobileMenuOpen(false);
                  setTimeout(() => handleScrollTo("#home"), 150);
                } else {
                  handleScrollTo("#home");
                }
              }}
            >
              {/* figure out the cumulative shift  */}
              <Image
                src="/images/BrimmLogoNav.png"
                alt="BrimmHQ Logo"
                fill
                priority
                className="object-contain"
              />
            </div>

            <ul className="hidden md:flex items-center gap-5 lg:gap-6">
              {navLinks.map((link) => (
                <li
                  key={link.id}
                  className={`cursor-pointer transition-colors duration-200 ${
                    activeSection === link.id
                      ? "text-primary font-bold"
                      : "hover:text-primary"
                  }`}
                  onClick={() => handleScrollTo(link.target)}
                >
                  {link.label}
                </li>
              ))}
            </ul>
            <div className="hidden md:flex items-center gap-5 lg:gap-6">
              {/* <button className="cursor-not-allowed">Login</button> */}
              <button
                onClick={() => setIsPilotModalOpen(true)}
                className="relative overflow-hidden group py-3.5 lg:py-4 xl:py-4.5 px-3.5 lg:px-5 xl:px-6 rounded-[50px] bg-primary text-surface-white font-semibold cursor-pointer"
              >
                <span className="absolute inset-y-0 left-0 w-0 bg-white/20 transition-all duration-300 ease-out group-hover:w-full z-0 pointer-events-none rounded-[5px]" />
                <span className="relative z-10">Join the Pilot</span>
              </button>
            </div>

            <div className="md:hidden">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#0052cc"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="cursor-pointer relative z-50 text-primary"
              >
                <path
                  d="M4 5h16"
                  className={`transition-all duration-300 origin-center ${isMobileMenuOpen ? "rotate-45 translate-y-[7px]" : ""}`}
                />
                <path
                  d="M4 12h16"
                  className={`transition-all duration-300 ${isMobileMenuOpen ? "opacity-0" : "opacity-100"}`}
                />
                <path
                  d="M4 19h16"
                  className={`transition-all duration-300 origin-center ${isMobileMenuOpen ? "-rotate-45 -translate-y-[7px]" : ""}`}
                />
              </svg>
            </div>
          </div>

          {/* Mobile Navigation Dropdown Menu */}
          <div
            className={`absolute left-0 right-0 bg-surface-white md:hidden transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] overflow-hidden shadow-[0_20px_40px_rgba(0,0,0,0.08)] -z-10 ${
              isMobileMenuOpen
                ? "opacity-100 translate-y-0 pointer-events-auto"
                : "opacity-0 -translate-y-8 pointer-events-none"
            }`}
            style={{
              top: `calc(100% - 1px)`,
              height: `calc(100vh - ${navHeight}px + 1px)`,
              transitionDelay: isMobileMenuOpen ? "0ms" : "300ms",
            }}
          >
            <div
              className="flex flex-col h-full px-6 pt-4 pb-8 overflow-y-auto"
              data-lenis-prevent
            >
              <ul className="flex flex-col gap-5">
                {navLinks.map((link, idx) => (
                  <li
                    key={link.id}
                    className={`text-[20px] tracking-tight font-semibold cursor-pointer transition-all duration-[600ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
                      isMobileMenuOpen
                        ? "opacity-100 translate-x-0"
                        : "opacity-0 -translate-x-12"
                    } ${
                      activeSection === link.id
                        ? "text-primary"
                        : "text-surface hover:text-primary"
                    }`}
                    style={{
                      transitionDelay: isMobileMenuOpen
                        ? `${100 + idx * 80}ms`
                        : `${(navLinks.length - idx) * 40}ms`,
                    }}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setTimeout(() => {
                        handleScrollTo(link.target);
                      }, 150);
                    }}
                  >
                    {link.label}
                  </li>
                ))}
              </ul>

              <div
                className={`mt-auto pt-5 pb-25 flex flex-col gap-4 transition-all duration-[800ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
                  isMobileMenuOpen
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-16"
                }`}
                style={{
                  transitionDelay: isMobileMenuOpen
                    ? `${100 + navLinks.length * 80}ms`
                    : "0ms",
                }}
              >
                {/* <button className="w-full py-4 px-6 rounded-[50px] outline outline-primary text-primary font-semibold text-lg cursor-not-allowed">
                  Login
                </button> */}
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false); // Close menu when modal opens
                    setIsPilotModalOpen(true);
                  }}
                  className="w-full py-4 px-6 rounded-[50px] bg-primary text-surface-white font-semibold text-lg cursor-pointer"
                >
                  Join the Pilot
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <PilotModal
        isOpen={isPilotModalOpen}
        onClose={() => setIsPilotModalOpen(false)}
      />
    </>
  );
};

export default NavBar;
